import { getDynamicApiUrl } from '../utils/themeLoader';

const base = () => getDynamicApiUrl();

const authHeaders = (token: string) => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
});

async function request<T>(url: string, options: RequestInit): Promise<T> {
    const res = await fetch(url, options);
    if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `HTTP ${res.status}`);
    }
    const text = await res.text();
    return text ? JSON.parse(text) : undefined;
}

// ── Auth ─────────────────────────────────────────────────────────────────────

export const adminInit = (email: string, password: string) =>
    request<{ preAuthToken: string; requiresTotpSetup: boolean }>(
        `${base()}/admin/init`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) }
    );

export const adminLogin = (email: string, password: string) =>
    request<{ preAuthToken: string; requiresTotpSetup: boolean }>(
        `${base()}/admin/login`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) }
    );

export const adminTotpSetup = (preAuthToken: string) =>
    request<{ qrUri: string; secret: string; backupCodes: string[] }>(
        `${base()}/admin/2fa/setup`,
        { method: 'POST', headers: authHeaders(preAuthToken) }
    );

export const adminTotpVerify = (preAuthToken: string, code: string) =>
    request<{ token: string; expiry: string }>(
        `${base()}/admin/2fa/verify`,
        { method: 'POST', headers: authHeaders(preAuthToken), body: JSON.stringify({ code }) }
    );

// ── Brokers ──────────────────────────────────────────────────────────────────

export const adminGetBrokers = (token: string) =>
    request<any[]>(`${base()}/admin/brokers`, { method: 'GET', headers: authHeaders(token) });

export const adminApproveBroker = (token: string, id: string) =>
    request<void>(`${base()}/admin/brokers/${id}/approve`, { method: 'PATCH', headers: authHeaders(token) });

export const adminRejectBroker = (token: string, id: string) =>
    request<void>(`${base()}/admin/brokers/${id}/reject`, { method: 'PATCH', headers: authHeaders(token) });

// ── Customers ────────────────────────────────────────────────────────────────

export const adminGetCustomers = (token: string) =>
    request<any[]>(`${base()}/admin/customers`, { method: 'GET', headers: authHeaders(token) });

// ── Quiz results ─────────────────────────────────────────────────────────────

export const adminGetQuizResults = (token: string) =>
    request<any[]>(`${base()}/admin/quiz-results`, { method: 'GET', headers: authHeaders(token) });

// ── Groups ───────────────────────────────────────────────────────────────────

export const adminGetQuizzes = (token: string) =>
    request<any[]>(`${base()}/admin/quizzes`, { method: 'GET', headers: authHeaders(token) });

export const adminGetGroups = (token: string) =>
    request<any[]>(`${base()}/admin/groups`, { method: 'GET', headers: authHeaders(token) });

export const adminCreateGroup = (token: string, name: string, slug: string) =>
    request<any>(`${base()}/admin/groups`, { method: 'POST', headers: authHeaders(token), body: JSON.stringify({ name, slug }) });

export const adminAssignQuizzes = (token: string, groupId: string, quizIds: number[]) =>
    request<any>(`${base()}/admin/groups/${groupId}/quizzes`, { method: 'PUT', headers: authHeaders(token), body: JSON.stringify({ quizIds }) });
